/**
 * Kennung dieses Browsertabs. Sie geht als Kopfzeile `x-client-id` an jeden schreibenden Aufruf und
 * beim Verbinden des SSE-Stroms mit - das Backend schliesst damit den Ausloeser eines
 * Schreibvorgangs von der Zustellung seines eigenen Ereignisses aus
 * (`backend/src/http/routes/events.ts`, `backend/src/sse/broadcast.ts`).
 *
 * ===================== Befund 13 (Task 4, Auffaelligkeiten-Entscheidungsvorlage) =====================
 *
 * `x-client-id` ist UNAUTHENTIFIZIERT UND FREI WAEHLBAR. Das Backend vergleicht den Header eines
 * Schreibvorgangs mit dem Header, den eine SSE-Verbindung beim Verbinden mitgeschickt hat - ein
 * einfacher Zeichenkettenvergleich, ohne jede Bindung an die Sitzung des Schreibers. Wer die
 * Kennung eines fremden Clients kennt oder erraet, kann sie in seine eigenen Schreibvorgaenge
 * setzen; dieser fremde Client bekommt die zugehoerigen Ereignisse dann NICHT und arbeitet
 * unbemerkt auf einem veralteten Stand weiter. Kein Datenabfluss, aber ein stiller
 * Aktualitaetsverlust.
 *
 * ENTSCHIEDEN am 2026-08-11 (Auftraggeber, Sammelstand
 * `docs/superpowers/decisions/2026-08-11-entscheidungen-auftraggeber.md`):
 *
 *   - **Option C ist die Sofortmassnahme und gilt**: die Kennung wird hier client-seitig
 *     gewuerfelt, je Browsertab, und beim Lesen aus dem `sessionStorage` auf ihre Form geprueft
 *     (siehe `KENNUNG_MUSTER` unten). Das verhindert VERSEHENTLICHE Kollisionen. Gegen eine
 *     ABSICHTLICHE Kollision hilft es nicht - wer den Header selbst setzt, kann jede Kennung
 *     waehlen.
 *   - **Option B ist als BACKEND-Folgetask vorgemerkt** (serverseitige Ableitung aus der Sitzung).
 *     Kommt sie, aendert sich AUSSCHLIESSLICH `clientKennung()` - es liest die Kennung dann aus der
 *     Anmeldeantwort statt sie selbst zu wuerfeln. Kein Aufrufer aendert sich: `api/basis.ts` und
 *     `api/ereignisse.ts` kennen nur diese eine Funktion.
 *
 * Die Kennung verraet NICHTS ueber Nutzer oder Sitzung (kein Nutzername, keine id, kein
 * Tokenanteil) - sie wird bei jedem Schreibvorgang mitgesendet und waere sonst ein unnoetiger
 * Kennzeichentraeger.
 *
 * "Je Browsertab" heisst `sessionStorage`, nicht `localStorage`: `sessionStorage` ist je Tab
 * getrennt und ueberlebt ein Neuladen der Seite. Mit `localStorage` teilten sich zwei Tabs
 * desselben Nutzers EINE Kennung - Tab B saehe dann die Aenderungen aus Tab A nie, weil das
 * Backend sie als "eigene" ausschliesst.
 */

const SPEICHER_SCHLUESSEL = "hubClientId";

/** Rueckfallebene, falls `sessionStorage` fehlt oder wirft (privater Modus, gesperrte Cookies). */
let imSpeicher: string | null = null;

function neueKennung(): string {
  const krypto = globalThis.crypto;
  if (krypto && typeof krypto.randomUUID === "function") {
    return krypto.randomUUID();
  }
  // Rueckfall ohne WebCrypto: Math.random ist hier vertretbar, weil die Kennung KEIN Geheimnis
  // ist (siehe Befund 13 oben - sie schuetzt nichts, sie unterscheidet nur Tabs). Waere sie ein
  // Geheimnis, gehoerte an diese Stelle ein Fehler statt eines Rueckfalls.
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Die zwei Formen, die `neueKennung()` erzeugen kann: eine UUID (v4, aus WebCrypto) oder der
 * Rueckfall `c-<zeit36>-<zufall36>`. Alles andere stammt nicht aus diesem Modul.
 *
 * `{0,10}` statt `{1,10}` seit dem Gesamtreview (2026-08-11, Minor 2): bei `Math.random() === 0`
 * ist `(0).toString(36).slice(2, 12)` LEER, `neueKennung()` liefert dann `c-<zeit36>-` - und das
 * Muster lehnte die eigene Ausgabe ab (nachgemessen an `"c-mso9xpr9-"`). Folge waere gewesen: die
 * Kennung ueberlebt kein Neuladen, weil `clientKennung()` den selbst geschriebenen Wert beim Lesen
 * verwirft und neu wuerfelt. Wahrscheinlichkeit vernachlaessigbar, ein Zeichen im Muster erledigt es.
 */
const KENNUNG_MUSTER =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|c-[0-9a-z]+-[0-9a-z]{0,10})$/i;

/** Die Kennung dieses Tabs; beim ersten Aufruf erzeugt, danach stabil (auch ueber ein Neuladen). */
export function clientKennung(): string {
  if (imSpeicher) return imSpeicher;
  try {
    const vorhanden = globalThis.sessionStorage?.getItem(SPEICHER_SCHLUESSEL);
    // Befund 13, Option C (entschieden 2026-08-11): nur ein Wert, den DIESES Modul erzeugt haben
    // kann, wird uebernommen. Ein von Hand hineingeschriebener oder aus einem anderen Tab
    // kopierter Wert wuerde die Zusage "zufaellig je Sitzung" still zunichtemachen - genau die
    // VERSEHENTLICHE Kollision, die Option C verhindern soll. Gegen eine ABSICHTLICHE Kollision
    // hilft das nicht (der Angreifer setzt den Header ohnehin selbst) - dafuer ist der
    // Backend-Folgetask vorgemerkt.
    if (vorhanden && KENNUNG_MUSTER.test(vorhanden)) {
      imSpeicher = vorhanden;
      return vorhanden;
    }
  } catch { // still: sessionStorage nicht verfuegbar (privates Fenster o. ae.) - unten weiter mit der reinen Speicherfassung
  }
  const kennung = neueKennung();
  imSpeicher = kennung;
  try {
    globalThis.sessionStorage?.setItem(SPEICHER_SCHLUESSEL, kennung);
  } catch { // still: nicht persistierbar - die Kennung gilt dann nur bis zum naechsten Neuladen; das ist ein Komfort-, kein Korrektheitsverlust (sie muss eindeutig sein, nicht dauerhaft)
  }
  return kennung;
}

/** Nur fuer Tests: verwirft die gemerkte Kennung, damit der naechste Aufruf neu erzeugt. */
export function setzeClientKennungZurueck(): void {
  imSpeicher = null;
  try {
    globalThis.sessionStorage?.removeItem(SPEICHER_SCHLUESSEL);
  } catch { // still: nur fuer Tests - ein nicht verfuegbarer sessionStorage ist hier derselbe Normalfall wie oben
  }
}
